import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ProfessionalStatus,
  professionalStatusChangeReasonLabels,
  professionalStatusLabels,
} from "../schema/professional-schema";
import { useProfessionalStatusLogs } from "../hooks/use-professional-status-logs";

const statusVariant = (status: ProfessionalStatus | null) => {
  if (status === ProfessionalStatus.ACTIVE) return "active" as const;
  if (status === ProfessionalStatus.FULLY_DISMISSED) return "destructive" as const;
  return "secondary" as const;
};

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

type Props = { professionalId: string };

// Every recorded status change for one professional, newest first (RH-506).
// Only the latest change is shown until the reader asks for the rest.
export default function ProfessionalStatusHistory({ professionalId }: Props) {
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProfessionalStatusLogs(professionalId);
  const [expanded, setExpanded] = useState(false);

  const logs = data?.pages.flatMap((p) => p.logs) ?? [];
  const total = data?.pages[0]?.total ?? logs.length;
  const visible = expanded ? logs : logs.slice(0, 1);
  const hiddenCount = Math.max(total - 1, 0);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-red-600">Could not load status history.</p>
    );
  }

  if (logs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No status changes recorded yet. History is kept from the day this was
        introduced; earlier changes were not tracked.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <ol className="divide-y divide-gray-200 rounded-md border border-gray-200">
        {visible.map((log) => (
          <li key={log.id} className="px-3 py-2.5 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              {log.previous_status ? (
                <Badge variant={statusVariant(log.previous_status)}>
                  {professionalStatusLabels[log.previous_status] ??
                    log.previous_status}
                </Badge>
              ) : (
                <span className="text-xs text-muted-foreground">No prior status</span>
              )}
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
              <Badge variant={statusVariant(log.new_status)}>
                {professionalStatusLabels[log.new_status] ?? log.new_status}
              </Badge>
              <span className="ml-auto text-xs text-muted-foreground">
                {formatWhen(log.created_at)}
              </span>
            </div>
            <div className="mt-1 text-xs text-gray-700">
              <span className="font-medium">
                {professionalStatusChangeReasonLabels[log.reason] ?? log.reason}
              </span>
              {log.changed_by_name && (
                <span className="text-muted-foreground">
                  {" "}
                  · by {log.changed_by_name}
                </span>
              )}
            </div>
            {log.note && (
              <p className="mt-1 whitespace-pre-wrap text-xs text-gray-600">
                {log.note}
              </p>
            )}
          </li>
        ))}
      </ol>
      <div className="flex items-center gap-2">
        {!expanded && hiddenCount > 0 && (
          <Button variant="outline" size="sm" onClick={() => setExpanded(true)}>
            Show {hiddenCount} more
          </Button>
        )}
        {expanded && hasNextPage && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        )}
        {expanded && logs.length > 1 && (
          <Button variant="ghost" size="sm" onClick={() => setExpanded(false)}>
            Show less
          </Button>
        )}
      </div>
    </div>
  );
}
